import React, { forwardRef, useRef, useState, useMemo, useCallback, useEffect, useImperativeHandle, } from "react";
import { Col, Cascader, Row, Input, Spin, Button } from "antd";
import { connect } from "dva";
import { getUrlParams } from "@/utils/utils";
import { getAllStates, getAllCities } from "@/services/addresses";
import { getAllDealers, getDealerById } from "@/services/dealer";
import { getAllEntitiesGroups, getEntityGroupDetails } from "@/services/entityGroups";
import { getAllTransporters, getTransporterById } from "@/services/transporter";
import { getAdminManagers, getAdminUserById } from "@/services/users_admin";
import useScreenDetector from "@/hooks/useScreenDetector";
import updateUrlFilterParams from "./utils/updateUrlFilterParams";
import useFilterOptions from "./utils/useFilterOptions";
import useSortOptions from "./utils/useSortOptions";
import DateRangeFilter from "./DateRangeFilter";
import FilterTags from "./FilterTags";
import { withoutSearch, withoutSorting, withoutPagination, DEFAULT_FILTERS, filtersByTable, flagStatusOptions, singleFiltersParent, } from "./constants";
const { Search } = Input;
const singleColumnFilters = [
    "dealerId",
    "managerId",
    "flagStatus",
    "isPublic",
    "rating",
    "companyGroupId",
    "transporterId",
    "vehicleType",
    "securePassValue",
    "securePassLogType",
];
const dropdownRender = (menus) => React.createElement("div", null, menus);
const SortAndFilter = forwardRef(({ configPerm, type, singleFilters, searchWidth, filterWidth, sortWidth, dateRangeWidth, columns: propColumns, onFiltersChange, onSearch, onReset, filterDateRange, includedShipmentStatuses, inputsColumns: propInputsColumns, marginLeft, sortingSeparator, children, securePassEnabled, }, ref) => {
    const { isMobile } = useScreenDetector(575);
    const hastLookedAtURLParams = useRef(false);
    const customSearchTimeoutRef = useRef(null);
    const lastTextSearched = useRef(null);
    const lastFetchedCities = useRef([]);
    const lastFetchedDealers = useRef([]);
    const lastFetchedEntityGroups = useRef([]);
    const lastFetchedTransporters = useRef([]);
    const lastFetchedManagers = useRef([]);
    const [states, setStates] = useState([]);
    const [stateNames, setStateNames] = useState({});
    const [statesLoading, setStatesLoading] = useState(false);
    const [cities, setCities] = useState([]);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const [dealers, setDealers] = useState([]);
    const [dealersName, setDealersName] = useState({});
    const [dealersLoading, setDealersLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [entitiesGroup, setEntitiesGroup] = useState([]);
    const [entityGroupNames, setEntityGroupNames] = useState({});
    const [entitiesGroupLoading, setEntitiesGroupLoading] = useState(false);
    const [transporters, setTransporters] = useState([]);
    const [transportersName, setTransportersName] = useState({});
    const [transportersLoading, setTransportersLoading] = useState(false);
    const [managers, setManagers] = useState([]);
    const [managersName, setManagersName] = useState({});
    const [managersLoading, setManagersLoading] = useState(false);
    const [customSearch, setCustomSearch] = useState(null);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const inputWidth = useMemo(() => ({
        search: isMobile ? "100%" : searchWidth,
        filter: isMobile ? "100%" : filterWidth,
        sort: isMobile ? "100%" : sortWidth,
        dateRange: isMobile ? "100%" : dateRangeWidth,
    }), [isMobile, searchWidth, filterWidth, sortWidth, dateRangeWidth]);
    const filterOptions = useFilterOptions({
        type,
        singleFilters,
        states,
        cities,
        dealers,
        entitiesGroup,
        transporters,
        managers,
        includedShipmentStatuses,
        loading: {
            states: statesLoading,
            cities: citiesLoading,
            dealers: dealersLoading,
            entitiesGroup: entitiesGroupLoading,
            transporters: transportersLoading,
            managers: managersLoading,
        },
        securePassEnabled,
    });
    const { sortOptions, columnNames } = useSortOptions({
        type,
        columns: propColumns,
    });
    const updateFilters = useCallback(newFilters => {
        // Reset page to 1 if any non-pagination filter changes
        const shouldResetPagination = Object.keys(newFilters).some(key => {
            // Skip pagination-related keys
            if (["page", "pageSize"].includes(key))
                return false;
            // Check if the filter value has changed
            return newFilters[key] !== filters[key];
        });
        const finalFilters = {
            ...newFilters,
            // Reset to page 1 if filters changed (except for explicit pagination changes)
            page: shouldResetPagination ? 1 : newFilters.page,
        };
        setFilters(finalFilters);
        updateUrlFilterParams(finalFilters);
        if (typeof onFiltersChange === "function") {
            onFiltersChange(finalFilters);
        }
    }, [onFiltersChange, filters]);
    const handleFilterMenuClick = useCallback(filter => {
        // Parent / Child / Value scenario (3 columns)
        let [parent, child, value] = filter;
        // Single column scenario
        if (singleFilters && singleFiltersParent[parent]) {
            child = parent;
            value = singleFiltersParent?.[parent]?.value ?? filter[1];
            parent = singleFiltersParent?.[parent]?.parent;
        }
        // Which filter keys is being modified
        const key = singleColumnFilters.indexOf(parent) === -1 ? child : parent;
        let newValue = value;
        if (singleColumnFilters.indexOf(parent) !== -1) {
            newValue = child;
        }
        // Three column scenario
        if (["location", "pickupLocation", "dropOffLocation"].includes(parent)) {
            if (child === "stateIds" || child === "dropOffStateId") {
                const prevValue = [...(filters.stateIds || [])];
                const alreadyIn = prevValue.indexOf(value) !== -1;
                if (alreadyIn) {
                    newValue = prevValue.filter(stateId => stateId !== value);
                }
                else {
                    newValue = [...prevValue, value];
                }
            }
            else if (child === "cities") {
                const prevValue = [...(filters.cities || [])];
                const alreadyIn = prevValue.indexOf(value) !== -1;
                if (alreadyIn) {
                    newValue = prevValue.filter(city => city !== value);
                    if (newValue.length === 0)
                        newValue = undefined;
                }
                else {
                    newValue = [...prevValue, value];
                }
            }
        }
        else if (parent === "flagStatus") {
            const index = flagStatusOptions.indexOf(child);
            newValue = index !== -1 ? index + 1 : newValue;
        }
        else if (parent === "rating") {
            if (child === "norated") {
                newValue = -1;
            }
            else {
                newValue = parseInt(child.split("-")[1], 10);
            }
        }
        const newFilters = {
            ...filters,
            [key]: newValue,
        };
        setCustomSearch("");
        updateFilters(newFilters);
    }, [filters, singleFilters, updateFilters]);
    const searchFilter = useCallback((inputValue, path) => {
        let { label } = path[path.length - 1];
        if (typeof label !== "string") {
            if (label?.props?.children) {
                // in case of autosled managers, try to search by email when typing into filter bar
                [, , label] = label.props.children;
            }
            else {
                label = String(label);
            }
        }
        return label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
    }, []);
    const handleCustomSearch = useCallback(inputValue => {
        if (customSearchTimeoutRef.current !== null) {
            clearTimeout(customSearchTimeoutRef.current);
        }
        if (inputValue === "") {
            setCustomSearch("");
        }
        else {
            customSearchTimeoutRef.current = setTimeout(() => {
                setCustomSearch(inputValue);
            }, 500);
        }
    }, []);
    const renderFilterOptions = useCallback((inputValue, path) => {
        const split = " / ";
        let option = "";
        for (let i = 0; i < path.length; i++) {
            option += path[i].label;
            option += split;
        }
        // remove last split
        option = option.slice(0, option.length - split.length);
        return option;
    }, []);
    const sortFilterOptions = useCallback((a, b, inputValue) => {
        const labelA = String(a[a.length - 1].label).toLowerCase();
        const labelB = String(b[b.length - 1].label).toLowerCase();
        const input = inputValue.toLowerCase();
        if (input.indexOf(labelA) > input.indexOf(labelB)) {
            return -1;
        }
        return 1;
    }, []);
    const handleSortMenuClick = useCallback(value => {
        if (value === undefined) {
            return;
        }
        if (value.length === 0) {
            updateFilters({ ...filters, sorting: "" });
            return;
        }
        const colKey = value[0];
        const sortOrder = value[1];
        const newSort = `${colKey}${sortingSeparator}${sortOrder}`;
        updateFilters({ ...filters, sorting: newSort });
        const params = new URLSearchParams(window.location.search);
        params.set("sorter", newSort);
        window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
    }, [filters, sortingSeparator, updateFilters]);
    const searchSort = useCallback((inputValue, path) => {
        const { label } = path[0];
        return label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;
    }, []);
    const resetFields = useCallback(() => {
        const newFilters = { ...DEFAULT_FILTERS };
        setCustomSearch("");
        setSearchText("");
        setFilters(newFilters);
        if (typeof onFiltersChange === "function") {
            onFiltersChange(newFilters);
        }
        const params = new URLSearchParams(window.location.search);
        params.delete("q");
        params.delete("filters");
        params.delete("sorter");
        params.set("page", newFilters.page.toString());
        params.set("pageSize", newFilters.pageSize.toString());
        window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
        if (typeof onReset === "function") {
            onReset();
        }
    }, [onReset, onFiltersChange]);
    const onRemoveFilter = useCallback((filterKey, value) => {
        let newValue;
        if (filterKey === "stateIds") {
            newValue = (filters.stateIds || []).filter(stateId => stateId !== value);
        }
        else if (filterKey === "cities") {
            newValue = (filters.cities || []).filter(city => city !== value);
        }
        if (Array.isArray(newValue) && newValue.length === 0) {
            newValue = undefined;
        }
        const newFilters = {
            ...filters,
            [filterKey]: newValue,
        };
        updateFilters(newFilters);
    }, [filters, updateFilters]);
    const onRemoveSorting = useCallback(() => {
        updateFilters({
            ...filters,
            sorting: "",
        });
        const params = new URLSearchParams(window.location.search);
        params.delete("sorter");
        window.history.replaceState({}, "", `${window.location.pathname}?${params}`);
    }, [filters, updateFilters]);
    const fetchCitiesBySearch = useCallback(async (filterText) => {
        const filterOptionsIds = filtersByTable[type];
        const cityFilters = ["city", "cities", "pickupCity", "dropOffCity"];
        if (cityFilters.every(id => !filterOptionsIds.includes(id))) {
            return;
        }
        let items = [];
        setCitiesLoading(true);
        try {
            const response = await getAllCities({ filterText, pageSize: 10 });
            items = response?.items || [];
        }
        catch (error) {
            console.error(error);
        }
        if (filters.city && items.indexOf(filters.city) === -1) {
            items.push(filters.city);
        }
        items.concat((filters.cities || []).filter(city => items.indexOf(city) === -1));
        lastFetchedCities.current = items;
        setCities(items);
        setCitiesLoading(false);
    }, [type, filters?.city, filters?.cities]);
    const fetchGroupsList = useCallback(async (filterText) => {
        const filterOptionsIds = filtersByTable[type];
        if (!configPerm["CoreService.CompanyGroups"] ||
            !hastLookedAtURLParams.current ||
            filterOptionsIds.indexOf("companyGroupId") === -1) {
            return;
        }
        setEntitiesGroupLoading(true);
        try {
            const response = await getAllEntitiesGroups({
                pageSize: 10,
                filter: filterText,
            });
            const { items } = response || { items: [] };
            const names = {};
            const entityGroupId = filters.companyGroupId;
            if (entityGroupId) {
                const filteringEntityGroup = lastFetchedEntityGroups.current.find((entityId) => entityId.id === entityGroupId);
                const fetchedOnLastSearch = items.find((entityId) => entityId.id === entityGroupId);
                if (!fetchedOnLastSearch && filteringEntityGroup) {
                    items.push({ ...filteringEntityGroup });
                }
                else if (!fetchedOnLastSearch && !filteringEntityGroup) {
                    const selectedEntityGroup = await getEntityGroupDetails(entityGroupId);
                    items.push(selectedEntityGroup);
                }
            }
            items.forEach((item) => {
                if (!item?.id || !item?.name) {
                    return;
                }
                names[item.id] = item.name;
            });
            lastFetchedEntityGroups.current = items;
            setEntitiesGroup(items);
            setEntityGroupNames(names);
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setEntitiesGroupLoading(false);
        }
    }, [type, configPerm, filters.companyGroupId]);
    const fetchStates = useCallback(() => {
        const filterOptionsIds = filtersByTable[type];
        const stateFilters = ["stateId", "stateIds", "pickupStateId", "dropOffStateId"];
        if (states.length > 0 ||
            !hastLookedAtURLParams.current ||
            stateFilters.every(id => !filterOptionsIds.includes(id))) {
            return;
        }
        getAllStates()
            .then(response => {
            const { items } = response || { items: [] };
            const names = {};
            items.forEach((item) => {
                if (!item?.id || !item?.shortName) {
                    return;
                }
                names[item.id] = item.shortName;
            });
            setStates(items);
            setStateNames(names);
        })
            .finally(() => {
            setStatesLoading(false);
        });
    }, [type, states.length]);
    const fetchDealers = useCallback(async (dealersCustomSearch) => {
        const filterOptionsIds = filtersByTable[type];
        if (!configPerm["CoreService.Dealers"] ||
            !hastLookedAtURLParams.current ||
            dealersCustomSearch === null ||
            filterOptionsIds.indexOf("dealerId") === -1) {
            return;
        }
        setDealersLoading(true);
        let items = [];
        try {
            const response = await getAllDealers({
                filter: dealersCustomSearch,
            });
            items = response?.items || [];
        }
        catch (error) {
            console.error(error);
        }
        const filterDealerId = filters.dealerId;
        if (filterDealerId) {
            const filteringDealer = lastFetchedDealers.current.filter((dealer) => dealer.id === filterDealerId)[0];
            const fetchedOnLastSearch = items.filter((dealer) => dealer.id === filterDealerId)[0];
            if (!fetchedOnLastSearch && filteringDealer) {
                items.push({ ...filteringDealer });
            }
            else if (!fetchedOnLastSearch && !filteringDealer) {
                const selectedDealer = await getDealerById(filterDealerId);
                items.push(selectedDealer);
            }
        }
        const names = {};
        items.forEach((item) => {
            if (!item?.id || !item?.name) {
                return;
            }
            names[item.id] = item.name;
        });
        lastFetchedDealers.current = items;
        setDealers(items);
        setDealersName(names);
        setDealersLoading(false);
    }, [type, configPerm, filters.dealerId]);
    const fetchManagers = useCallback(async (managersCustomSearch) => {
        const filterOptionsIds = filtersByTable[type];
        if (!configPerm["CoreService.Dealers"] ||
            !hastLookedAtURLParams.current ||
            managersCustomSearch === null ||
            filterOptionsIds.indexOf("managerId") === -1) {
            return;
        }
        setManagersLoading(true);
        let items = [];
        // always fetch admins
        try {
            const response = await getAdminManagers({
                filter: managersCustomSearch,
            });
            items = response || [];
        }
        catch (error) {
            console.error(error);
        }
        const filterManagerId = filters.managerId;
        if (filterManagerId) {
            const filteringManager = lastFetchedManagers.current.filter(manager => manager.id === filterManagerId)[0];
            const fetchedOnLastSearch = items.filter(manager => manager.id === filterManagerId)[0];
            if (!fetchedOnLastSearch && filteringManager) {
                items.push({ ...filteringManager });
            }
            else if (!fetchedOnLastSearch && !filteringManager) {
                const selectedManager = await getAdminUserById(filterManagerId);
                items.push(selectedManager);
            }
        }
        const names = {};
        items.forEach((item) => {
            if (!item?.id || !item?.fullName) {
                return;
            }
            names[item.id] = item.fullName;
        });
        lastFetchedManagers.current = items;
        setManagers(items);
        setManagersName(names);
        setManagersLoading(false);
    }, [type, configPerm, filters.managerId]);
    const fetchTransporters = useCallback(async (transportersCustomSearch) => {
        const filterOptionsIds = filtersByTable[type];
        if (!configPerm["CoreService.Transporters"] ||
            !hastLookedAtURLParams.current ||
            transportersCustomSearch === null ||
            filterOptionsIds.indexOf("transporterId") === -1) {
            return;
        }
        setTransportersLoading(true);
        let items = [];
        try {
            const response = await getAllTransporters({
                filter: transportersCustomSearch,
            });
            items = response?.items || [];
        }
        catch (error) {
            console.error(error);
        }
        const filterTransporterId = filters.transporterId;
        if (filterTransporterId) {
            const filteringTransporter = lastFetchedTransporters.current.filter(transporter => transporter.id === filterTransporterId)[0];
            const fetchedOnLastSearch = items.filter(transporter => transporter.id === filterTransporterId)[0];
            if (!fetchedOnLastSearch && filteringTransporter) {
                items.push({ ...filteringTransporter });
            }
            else if (!fetchedOnLastSearch && !filteringTransporter) {
                const selectedTransporter = await getTransporterById(filterTransporterId);
                items.push(selectedTransporter);
            }
        }
        const names = {};
        items.forEach((item) => {
            if (!item?.id || !item?.name) {
                return;
            }
            names[item.id] = item.name;
        });
        lastFetchedTransporters.current = items;
        setTransporters(items);
        setTransportersName(names);
        setTransportersLoading(false);
    }, [type, configPerm, filters.transporterId]);
    useEffect(() => {
        if (!hastLookedAtURLParams.current || customSearch === null || lastTextSearched.current === customSearch) {
            return;
        }
        lastTextSearched.current = customSearch;
        // We do not need to search for a state because we can fetch
        // all states at once
        fetchStates();
        fetchCitiesBySearch(customSearch);
        fetchDealers(customSearch);
        fetchGroupsList(customSearch);
        fetchTransporters(customSearch);
        fetchManagers(customSearch);
    }, [
        customSearch,
        fetchCitiesBySearch,
        fetchStates,
        fetchDealers,
        fetchGroupsList,
        fetchTransporters,
        fetchManagers,
    ]);
    useEffect(() => {
        if (hastLookedAtURLParams.current) {
            return;
        }
        hastLookedAtURLParams.current = true;
        const { sorter, filters: paramsFilters, q, pageSize, page, } = getUrlParams(["sorter", "filters", "q", "pageSize", "page"]) ?? {};
        if (!q) {
            setCustomSearch("");
        }
        const valuesFromUrl = [sorter, paramsFilters, q, pageSize, page];
        const hasAnUrlValue = valuesFromUrl.some(value => value !== undefined && value !== null);
        if (!hasAnUrlValue && withoutPagination.includes(type)) {
            return;
        }
        const urlFilters = (paramsFilters || "").split("_");
        const intPageSize = parseInt(pageSize || "", 10) ?? DEFAULT_FILTERS.pageSize;
        const intPage = parseInt(page || "", 10) ?? DEFAULT_FILTERS.page;
        const newFilters = {
            ...DEFAULT_FILTERS,
            filter: q ?? "",
            sorting: sorter ?? "",
            page: !Number.isNaN(intPage) ? intPage : DEFAULT_FILTERS.page,
            pageSize: !Number.isNaN(intPageSize) ? intPageSize : DEFAULT_FILTERS.pageSize,
        };
        setSearchText(q || "");
        urlFilters.forEach((filterStr) => {
            const [name, value] = filterStr.split(":");
            if (name === "" || !name) {
                return;
            }
            let newValue = value;
            if (value === "true") {
                newValue = true;
            }
            else if (value === "false") {
                newValue = false;
            }
            if (name === "stateIds" || name === "cities") {
                if (typeof newValue === "boolean" || typeof newValue === "number" || typeof newValue === "undefined") {
                    newValue = [];
                }
                else {
                    newValue = newValue?.split(",") || [];
                }
            }
            newFilters[name] = newValue;
        });
        lastTextSearched.current = null;
        updateFilters(newFilters);
    }, [type, updateFilters]);
    useImperativeHandle(ref, () => ({
        getFilters: () => filters,
        setFilters: (newFilters) => {
            if (typeof newFilters === "function") {
                const proccesedFilters = newFilters(filters);
                if (typeof proccesedFilters.then === "function") {
                    // @ts-ignore
                    proccesedFilters.then((asyncProccesedFilters) => {
                        updateFilters(asyncProccesedFilters);
                    });
                }
                else {
                    updateFilters(proccesedFilters);
                }
            }
            else {
                updateFilters(newFilters);
            }
        },
        getPaginationFilters: () => ({
            page: filters.page,
            pageSize: filters.pageSize,
        }),
        setPaginationFilters: (newFilters) => {
            window.scrollTo(0, 0);
            updateFilters({
                ...newFilters,
                page: newFilters.page ?? DEFAULT_FILTERS.page,
                pageSize: newFilters.pageSize ?? DEFAULT_FILTERS.pageSize,
            });
        },
    }), [filters, updateFilters]);
    const { searchVisible, filtersVisible, sortVisible, resetButtonVisible, inputsColumns } = useMemo(() => {
        const isSearchVisible = withoutSearch.indexOf(type) === -1;
        const isFiltersVisible = filtersByTable[type]?.length > 0;
        const isSortVisible = withoutSorting.indexOf(type) === -1;
        const isResetButtonVisible = isSearchVisible || isFiltersVisible || isSortVisible;
        let totalColumns = 24;
        const resetButtonColumns = 4;
        totalColumns -= isResetButtonVisible ? resetButtonColumns : 0;
        const inputsToRender = [isSearchVisible, isFiltersVisible, isSortVisible, filterDateRange, children].filter(value => value).length;
        const newInputsColumns = {
            xs: propInputsColumns?.xs ?? 24,
            sm: propInputsColumns?.sm ?? Math.floor(totalColumns / inputsToRender),
            lg: propInputsColumns?.lg ?? 6,
        };
        // console.log(newInputsColumns, totalColumns, inputsToRender);
        return {
            searchVisible: isSearchVisible,
            filtersVisible: isFiltersVisible,
            sortVisible: isSortVisible,
            resetButtonVisible: isResetButtonVisible,
            inputsColumns: newInputsColumns,
        };
    }, [type, filterDateRange, propInputsColumns, children]);
    return (React.createElement(Col, { xs: 24, style: { marginLeft } },
        React.createElement(Row, { gutter: 8, style: { flex: 1 } },
            searchVisible && (React.createElement(Col, { xs: inputsColumns.xs, sm: inputsColumns.sm, lg: inputsColumns.lg, style: {
                    maxWidth: inputWidth.search,
                } },
                React.createElement(Search, { placeholder: "Search", onSearch: value => {
                        updateFilters({
                            ...filters,
                            filter: value,
                        });
                        if (typeof onSearch === "function") {
                            onSearch(value);
                        }
                    }, onChange: event => {
                        setSearchText(event.target.value);
                    }, value: searchText, style: {
                        maxWidth: inputWidth.search,
                        marginBottom: 16,
                    } }))),
            children && (React.createElement(Col, { xs: inputsColumns.xs, sm: inputsColumns.sm, lg: inputsColumns.lg, style: { maxWidth: inputWidth.search, marginBottom: 16 } }, children)),
            filtersVisible && (React.createElement(Col, { xs: inputsColumns.xs, sm: inputsColumns.sm, lg: inputsColumns.lg, style: { maxWidth: inputWidth.filter, marginBottom: 16 } },
                React.createElement(Cascader, { value: [], onSearch: handleCustomSearch, allowClear: false, expandTrigger: "hover", placeholder: "Filter", style: { width: "100%" }, onChange: handleFilterMenuClick, options: filterOptions, dropdownRender: dropdownRender, showSearch: {
                        limit: false,
                        filter: searchFilter,
                        render: renderFilterOptions,
                        sort: sortFilterOptions,
                    }, notFoundContent: statesLoading ||
                        citiesLoading ||
                        entitiesGroupLoading ||
                        dealersLoading ||
                        transportersLoading ||
                        managersLoading ? (React.createElement("div", { style: {
                            display: "flex",
                            flex: 1,
                            justifyContent: "center",
                            alignItems: "center",
                            minWidth: "80px",
                            minHeight: "80px",
                        } },
                        React.createElement(Spin, null))) : null }))),
            sortVisible && (React.createElement(Col, { xs: inputsColumns.xs, sm: inputsColumns.sm, lg: inputsColumns.lg, style: { maxWidth: inputWidth.sort, marginBottom: 16 } },
                React.createElement(Cascader, { value: [], allowClear: false, expandTrigger: "hover", placeholder: "Sort", style: { width: "100%" }, onChange: handleSortMenuClick, options: sortOptions, dropdownRender: dropdownRender, showSearch: {
                        filter: searchSort,
                        render: renderFilterOptions,
                        sort: sortFilterOptions,
                    } }))),
            filterDateRange && (React.createElement(Col, { xs: inputsColumns.xs, sm: inputsColumns.sm, lg: inputsColumns.lg, style: { maxWidth: inputWidth.dateRange, marginBottom: 16 } },
                React.createElement(DateRangeFilter, { style: { width: "100%" }, onChangeFilters: (newFilter) => {
                        updateFilters({ ...filters, ...newFilter });
                    } }))),
            resetButtonVisible && (React.createElement(Col, { xs: 24, sm: 4, style: { marginBottom: 16 } },
                React.createElement(Button, { style: { minWidth: 75, width: isMobile ? "100%" : "auto" }, type: "primary", onClick: resetFields }, isMobile ? "Reset filters" : "Reset")))),
        React.createElement(Row, { style: { flex: 1, marginBottom: 10 } },
            React.createElement(FilterTags, { filters: filters, stateNames: stateNames, dealersName: dealersName, entityGroupNames: entityGroupNames, transportersName: transportersName, managersName: managersName, sortingSeparator: sortingSeparator, columnNames: columnNames, onRemoveFilter: onRemoveFilter, onRemoveSorting: onRemoveSorting }))));
});
SortAndFilter.displayName = "SortAndFilter";
SortAndFilter.defaultProps = {
    singleFilters: false,
    searchWidth: "220px",
    filterWidth: "180px",
    sortWidth: "180px",
    dateRangeWidth: "240px",
    onFiltersChange: null,
    onSearch: null,
    onReset: null,
    filterDateRange: false,
    includedShipmentStatuses: undefined,
    inputsColumns: undefined,
    marginLeft: "20px",
    sortingSeparator: ":",
    children: null,
    securePassEnabled: false,
};
export default connect(({ configs }) => ({
    securePassEnabled: configs.securePass.enabled,
    configPerm: configs.configPerm,
}), null, null, { forwardRef: true })(SortAndFilter);
